/* ═══════════════════════════════════════════════════════
   Used Apps Icon System — Integration brand logos
   Hierarchy: Local assets → Composio CDN → Generic fallback
   ═══════════════════════════════════════════════════════ */

// Composio logo CDN pattern
const COMPOSIO_CDN = 'https://logos.composio.dev/api';

// Generic fallback icon (neutral grid icon for unknown apps)
const GENERIC_FALLBACK = '/app-icons/generic-app.svg';

// ── Local icon overrides (apps without Composio logos) ──
// These are stored in /public/app-icons/ as downloaded brand favicons
const LOCAL_ICON_MAP = {
  'jobber':                   '/app-icons/jobber.png',
  'trustpilot':               '/app-icons/trustpilot.png',
  'googlebusinessprofile':    '/app-icons/google-business-profile.png',
  'housecallpro':             '/app-icons/housecallpro.png',
  'myob':                     '/app-icons/myob.png',
};

// ── Name → slug mapping for Composio CDN ──
const SLUG_MAP = {
  'Gmail':                    'gmail',
  'Google Workspace':         'googleworkspace',
  'Slack':                    'slack',
  'Sheets':                   'googlesheets',
  'Google Sheets':            'googlesheets',
  'Calendar':                 'googlecalendar',
  'Google Calendar':          'googlecalendar',
  'Outlook':                  'outlook',
  'Outlook Calendar':         'outlook',
  'Twilio':                   'twilio',
  'Twilio SMS':               'twilio',
  'Telnyx':                   'telnyx',
  'Webhook':                  'webhook',
  'Salesforce':               'salesforce',
  'Microsoft 365':            'microsoft365',
  'Xero':                     'xero',
  'QuickBooks':               'quickbooks_sandbox',
  'Export':                    'googledrive',
  'HubSpot':                  'hubspot',
  'Airtable':                 'airtable',
  'Airtable CRM':             'airtable',
  'Zapier':                   'zapier',
  'Stripe':                   'stripe',
  'Notion':                   'notion',
  'Drive':                    'googledrive',
  'Google Drive':             'googledrive',
  'Calendly':                 'calendly',
  'PandaDoc':                 'pandadoc',
  'Pipedrive':                'pipedrive',
  'Mailchimp':                'mailchimp',
  'ServiceM8':                'servicem8',
  'Google Business Profile':  'googlebusinessprofile',
  'Jobber':                   'jobber',
  'Trustpilot':               'trustpilot',
  'Housecall Pro':            'housecallpro',
  'MYOB':                     'myob',
  'Google Docs':              'googledocs',
};

// ── Alias handling (normalize variant names) ──
const ALIAS_MAP = {
  'google business':          'Google Business Profile',
  'gbp':                      'Google Business Profile',
  'quickbooks online':        'QuickBooks',
  'qbo':                      'QuickBooks',
  'service m8':               'ServiceM8',
  'housecall':                'Housecall Pro',
  'house call pro':           'Housecall Pro',
  'google cal':               'Google Calendar',
  'gcal':                     'Google Calendar',
  'g sheets':                 'Google Sheets',
  'gsheets':                  'Google Sheets',
  'gdrive':                   'Google Drive',
  'ms outlook':               'Outlook',
  'microsoft outlook':        'Outlook',
  'airtable crm':             'Airtable CRM',
  'twilio sms':               'Twilio SMS',
};

/**
 * Resolve an integration name to its canonical form.
 */
function resolveAlias(name) {
  if (!name) return name;
  const lower = name.trim().toLowerCase();
  return ALIAS_MAP[lower] || name.trim();
}

/**
 * Get logo URL for an integration name.
 * Hierarchy: Local asset → Composio CDN → Generic fallback
 */
export function getAppLogo(integrationName) {
  if (!integrationName) return GENERIC_FALLBACK;

  const canonical = resolveAlias(integrationName);
  const slug = SLUG_MAP[canonical] || canonical.toLowerCase().replace(/\s+/g, '');

  // 1. Check local icon override
  if (LOCAL_ICON_MAP[slug]) {
    return LOCAL_ICON_MAP[slug];
  }

  // 2. Use Composio CDN
  return `${COMPOSIO_CDN}/${slug}`;
}

/**
 * Get the generic fallback icon URL (for onError handlers).
 */
export function getGenericAppIcon() {
  return GENERIC_FALLBACK;
}

/**
 * Get app badge config for an integration name.
 */
export function getAppBadge(integrationName) {
  if (!integrationName) return null;
  const canonical = resolveAlias(integrationName);
  return {
    label: canonical,
    logo: getAppLogo(canonical),
    slug: SLUG_MAP[canonical] || canonical.toLowerCase().replace(/\s+/g, ''),
  };
}

/**
 * Extract used apps from template data.
 */
export function extractUsedApps(template) {
  const apps = [];
  const seen = new Set();

  for (const name of (template.marketplace_integrations || [])) {
    const key = resolveAlias(name);
    if (!seen.has(key)) {
      seen.add(key);
      apps.push(getAppBadge(key));
    }
  }

  return apps;
}
